import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Button,
  ButtonVariant,
  CmhcFlexIcon,
  CmhcIcon,
  CmhcIconSize,
  Modal
} from '@cmhc-schl/hearth-ui';
import { useTranslation } from 'react-i18next';
import { Loan, PoolProcessingStatusCode, PortfolioInsuranceType } from '../ForceStatusFlyout/types';
import { getLoanStatusIconProps } from './Utils/ErrorIconUtil/errorIconUtil';
import { FlyoutType } from '../ForceStatusFlyout/Utils/utils';
import { ForceStatusPill } from '../ForceStatusFlyout/ForceStatusPill';
import { ErrorCodeMessage } from './Utils/ErrorCodes';
import { getVariableText } from '../ForceStatusFlyout/components/SuccessResult';
import styles from './style.module.scss';
import { MemoizedHbtDataTableCell } from 'Feature/V2/Shared/MemoizedHbtDataTableCell';
import { duplicateNumberErrorCode } from './Utils/const';

type ErrorLoansModalProps = {
  open: boolean;
  loans: Loan[];
  eligibleLoans: Loan[];
  onClose: () => void;
  onProceed?: (elibleLoans: Loan[]) => void;
  title?: string;
  errorModalIntroText?: string;
  pptCode?: string;
  proceedText?: string;
  cancelText?: string;
  copyLabel?: string;
  copiedLabel?: string;
  isAddRemarks?: boolean;
  loansTypeToBeAdded?: string;
  errorModalHeader?: string;
  errorModalProceedText?: string;
  typeOfFlyout?: FlyoutType;
  copyButtonText?: string | VariableText;
  copiedButtonText?: string | VariableText;
  typeOfPortfolioInsurance?: PortfolioInsuranceType;
};

const renderErrorLegend = (
  loans: { errorCode?: string }[],
  errorCodes: string[],
  message: string,
  displayCode: string
) => {
  const shouldShow = loans?.some((loan) => errorCodes.includes(loan.errorCode ?? ''));

  if (!shouldShow) return null;

  const iconProps = getLoanStatusIconProps(displayCode);
  if (!iconProps) return null;

  return (
    <Box display="flex" alignItems="start" gap={1} mt={1}>
      <CmhcFlexIcon
        height={20}
        width={20}
        size={CmhcIconSize.SMALL}
        type={iconProps.iconType}
        className={iconProps.className}
      />
      <Typography variant="body2">{message}</Typography>
    </Box>
  );
};

interface VariableText {
  singular: string;
  plural: string;
}

const ErrorLoansModal: React.FC<ErrorLoansModalProps> = ({
  open,
  loans,
  eligibleLoans,
  onClose,
  onProceed,
  isAddRemarks,
  errorModalIntroText,
  pptCode,
  loansTypeToBeAdded,
  copyButtonText,
  copiedButtonText,
  typeOfPortfolioInsurance,
  errorModalHeader,
  errorModalProceedText,
  typeOfFlyout
}) => {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<number | null>(null);
  const { t } = useTranslation('HbtMasterPool');
  const copiedKeyBase = 'Loan-Numbers-Copied-Button-Text';
  const copyKeyBase = 'Copy-Loan-Numbers-Button-Text';
  const copiedKey = loans.length === 1 ? `${copiedKeyBase}-Singular` : `${copiedKeyBase}-Plural`;
  const copyKey = loans.length === 1 ? `${copyKeyBase}-Singular` : `${copyKeyBase}-Plural`;
  const isCancellation = pptCode === PoolProcessingStatusCode.CANCELLED;

  const handleCopy = async () => {
    const text = loans
      .map((loan) => loan.validationReviewLoanData?.accountNumber ?? loan.accountNumber ?? '')
      .join('\n');
    await navigator.clipboard.writeText(text);
    setCopied(true);
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => setCopied(false), 5000);
  };

  const isAddRemarksFlyout = typeOfFlyout === 'updateRemark';

  const buttonText = isAddRemarksFlyout
    ? t('Add-Remarks-Flyout-Heading')
    : t('Add-Loans-Proceed-Button-Text');

  const getErrorMsg = (type: string, isCancellation?: boolean) => {
    if (type === 'duplicates') {
      if (isAddRemarksFlyout) {
        return isCancellation
          ? t('Duplicate-Loans-Cancellations-For-Add-Remarks')
          : t('Duplicate-Loans-New-Portfolio-For-Add-Remarks');
      }
      return isCancellation
        ? t('Duplicate-Loans-Cancellations')
        : t('Duplicate-Loans-New-Portfolio');
    }

    return t('Duplicate-CMHC-Number');
  };

  const duplicateNumberErrorMsg =
    getErrorMsg('duplicates', isCancellation) ?? t('Duplicate-CMHC-Number');

  useEffect(
    () => () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    },
    []
  );

  return (
    <Modal
      id={styles.errorLoansModal}
      isActive={open}
      onDismissalCallback={onClose}
      title={{ value: errorModalHeader ?? t('Add-Loans-Error-Modal-Header-Text') }}
      bodyContent={
        <Box
          sx={{ maxWidth: 576, maxHeight: 442, width: '100%', overflow: 'auto', borderRadius: 1 }}
        >
          <Typography variant="body2" sx={{ mb: 2 }}>
            {errorModalIntroText ?? t('Add-Loans-Error-Modal-Body-Text')}
          </Typography>
          <Table stickyHeader>
            <TableHead className={styles.errorLoansModalTableHead}>
              <TableRow className={styles.errorLoansTableRow}>
                <TableCell className={styles.errorLoansModalTableCell}>
                  {loansTypeToBeAdded ?? t('CMHC-Account-Number')}
                </TableCell>
                {isAddRemarks ? (
                  <TableCell className={styles.errorLoansModalTableCell}>
                    <Stack direction="row" gap={1}>
                      {t('Remarks')}
                      <Tooltip
                        title={t('Remarks-Table-Header-Tooltip')}
                        placement="top-start"
                        arrow
                        slotProps={{
                          tooltip: {
                            className: styles.requestOverviewDetailsToolTip
                          },
                          arrow: {
                            className: styles.tooltipArrow
                          }
                        }}
                      >
                        <div>
                          <CmhcIcon size={CmhcIconSize.SMALL} type="icon_info_outlined" />
                        </div>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                ) : (
                  <TableCell className={styles.errorLoansModalTableCell}>{t('Status')}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {loans.map((loan) => {
                const iconProps = getLoanStatusIconProps(loan.errorCode);
                return (
                  <TableRow key={loan.id} className={styles.errorLoansModalTableRow}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {loan.validationReviewLoanData?.accountNumber ?? loan.accountNumber ?? ''}
                        {iconProps && (
                          <CmhcFlexIcon
                            height={16}
                            width={16}
                            size={CmhcIconSize.SMALL}
                            type={iconProps.iconType}
                            className={iconProps.className}
                          />
                        )}
                      </Box>
                    </TableCell>
                    {isAddRemarks ? (
                      <TableCell className={styles.addRemarksCell}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <MemoizedHbtDataTableCell
                            dataValue={String(loan.lastRemark)}
                            cellContent={
                              <Box title={loan.lastRemark} className={styles.loansRemark}>
                                {loan.lastRemark}
                              </Box>
                            }
                          />
                        </Box>
                      </TableCell>
                    ) : (
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {loan.status ? (
                            <ForceStatusPill
                              typeOfPortfolioInsurance={typeOfPortfolioInsurance!}
                              status={loan.status ?? ''}
                            />
                          ) : (
                            <CmhcIcon size={CmhcIconSize.SMALL} type="icon_indeterminate" />
                          )}
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              <TableRow className={styles.errorLoansModalTableRow}>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}></Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t(loans.length === 1 ? 'ItemsSingular' : 'ItemsPlural', {
                        count: loans.length
                      })}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Box mt={2} display="flex" flexDirection="column" gap={1}>
            {renderErrorLegend(
              loans,
              duplicateNumberErrorCode,
              duplicateNumberErrorMsg,
              'ERR_00002'
            )}
          </Box>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}
          >
            <Button
              customStyles={{ textDecoration: 'none' }}
              name="copyButton"
              text={
                copied
                  ? (getVariableText(loans.length, copiedButtonText) ?? t(copiedKey))
                  : (getVariableText(loans.length, copyButtonText) ?? t(copyKey))
              }
              ariaText={{
                value: copied
                  ? (getVariableText(loans.length, copiedButtonText) ?? t(copiedKey))
                  : (getVariableText(loans.length, copyButtonText) ?? t(copyKey))
              }}
              variant={ButtonVariant.TERTIARY}
              onClick={handleCopy}
              leadingIcon={
                copied
                  ? () => (
                      <CmhcIcon
                        size={CmhcIconSize.SMALL}
                        type="icon_check"
                        className={styles.copiedIcon}
                      />
                    )
                  : () => <CmhcIcon size={CmhcIconSize.SMALL} type="icon_copy" />
              }
            />
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>
            {errorModalProceedText ?? t('Add-Loans-Proceed-Text')}
          </Typography>
        </Box>
      }
      modalButtons={{
        primaryButton: {
          name: 'primary',
          text: { value: buttonText },
          ariaText: { value: t('Add-Loans-Proceed-Button-Text') },
          onClick: () => {
            onProceed && onProceed(eligibleLoans);
          }
        },
        secondaryButton: {
          name: 'secondary',
          text: { value: t('Cancel') },
          ariaText: { value: t('Cancel') },
          onClick: onClose
        }
      }}
      shouldCloseOnBackgroundClick={false}
    />
  );
};

export default ErrorLoansModal;
